import { random } from 'lodash'
import { useGetRewardRedemptionByRewardProgram } from 'hooks/api/Rewards/useGetRewardRedemptionByRewardProgram'
import { DataTable } from 'components/Shared/DataTable'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'
import { useMemo } from 'react'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { useSetMe } from 'stores/useMeQuery'
import { type GridColDef } from '@mui/x-data-grid'
import { useLayout } from 'hooks/useLayout'
import { dataTablePromotionDateFormatter } from 'utils/util'

export const RedemptionSection = ({
  rewardProgramId,
  daysDisplayed,
}: {
  rewardProgramId: number
  daysDisplayed: number
}) => {
  const { isMobile } = useLayout()
  const getStartDate = (daysDisplayed: number) => {
    const today = new Date()
    const daysMap: Record<number, () => void> = {
      7: () => today.setDate(today.getDate() - 7),
      30: () => today.setDate(today.getDate() - 30),
      90: () => today.setMonth(today.getMonth() - 3),
      365: () => {
        today.setMonth(0)
        today.setDate(1)
      },
    }

    daysMap[daysDisplayed]?.()
    return today
  }

  const startTransactionDate = useMemo(() => {
    return getStartDate(daysDisplayed).toLocaleString()
  }, [daysDisplayed])

  const endTransactionDate = useMemo(() => {
    return new Date().toLocaleString()
  }, [])

  const redemptionQuery = useGetRewardRedemptionByRewardProgram({
    rewardProgramId,
    startTransactionDate,
    endTransactionDate,
  })

  const RedemptionColumns: GridColDef[] = [
    {
      field: 'patronName',
      headerName: 'Patron Name',
      minWidth: 175,
      flex: 1,
      valueGetter: (params) => params.row.patronName,
    },
    {
      field: 'patronId',
      headerName: 'Patron Id',
      minWidth: 130,
      flex: 1,
      valueGetter: (params) =>
        isMobile ? `Patron Id: ${params.row.patronId}` : params.row.patronId,
    },
    {
      field: 'rewardItemId',
      headerName: 'Reward Item Id',
      minWidth: 175,
      flex: 1,
      valueGetter: (params) =>
        isMobile
          ? `Reward Item Id: ${params.row.rewardItemId}`
          : params.row.rewardItemId,
    },
    {
      field: 'rewardItemName',
      headerName: 'Reward Item Name',
      minWidth: 210,
      flex: 1,
      valueGetter: (params) =>
        isMobile
          ? `Reward Item: ${params.row.rewardItemName}`
          : params.row.rewardItemName,
    },
    {
      field: 'id',
      headerName: 'Reference #',
      minWidth: 200,
      flex: 0.75,
      valueGetter: (params) =>
        isMobile ? `Reference #: ${params.row.id}` : params.row.id,
    },
    {
      field: 'licensedEstablishmentName',
      headerName: 'Licensed Establishment Name',
      minWidth: 300,
      flex: 1,
      valueGetter: (params) =>
        isMobile
          ? `LE Name: ${params.row.licensedEstablishmentName ?? 'N/A'}`
          : params.row.licensedEstablishmentName,
    },
    {
      field: 'pointCost',
      headerName: 'Points Cost',
      minWidth: 150,
      flex: 1,
      valueGetter: (params) =>
        isMobile
          ? `Points Cost: ${params.row.pointCost}`
          : params.row.pointCost,
    },
    {
      field: 'timeOfredemption',
      headerName: 'Time of Redemption',
      minWidth: 220,
      flex: 1,
      valueGetter: (params) => params.row.timeOfredemption,
      valueFormatter: dataTablePromotionDateFormatter(
        isMobile,
        'Time of Redemption'
      ),
    },
    {
      field: 'rewardProgramName',
      headerName: 'Reward Program Name',
      minWidth: 250,
      flex: 1,
      valueGetter: (params) =>
        isMobile
          ? `Reward Program: ${params.row.rewardProgramName}`
          : params.row.rewardProgramName,
    },
  ]

  let RedemptionColumnsModified = RedemptionColumns
  const { passedMe } = useSetMe()
  const meQuery = passedMe

  const selectedCorporateAccountId =
    useCurrentCorporateAccountStore().currentCorporateAccountId

  if (redemptionQuery.isError) {
    return <p>An error occurred.</p>
  }

  if (redemptionQuery.isPending) {
    return <ActivityIndicator />
  }

  const leData =
    meQuery?.data?.user?.licensedEstablishments?.find(
      (x) =>
        x.licensedEstablishmentId ===
        meQuery?.data?.licensedEstablishments?.find(
          (x) => x.corporateAccountId === selectedCorporateAccountId
        )?.id
    ) ?? undefined

  const showPatronName = leData?.reportsAccessToPIIPermission
  if (!showPatronName) {
    RedemptionColumnsModified = RedemptionColumns.slice(1)
  }

  return (
    <DataTable
      sortFieldName="patronId"
      sort="asc"
      columns={RedemptionColumnsModified}
      rows={
        redemptionQuery.data?.map((redemption) => {
          let displayPatronName = ''
          if (showPatronName) {
            displayPatronName =
              redemption.patron?.firstName + ' ' + redemption.patron?.lastName
          }
          return {
            patronId: redemption.patronId,
            patronName: displayPatronName,
            rewardItemId: redemption.rewardsCatalogReward?.rewardId,
            rewardItemName: redemption.rewardsCatalogReward?.reward?.name,
            licensedEstablishmentName:
              redemption?.licensedEstablishment?.standardName,
            pointCost: redemption.rewardsCatalogReward?.amount,
            timeOfredemption: redemption.createdOn,
            rewardProgramName: redemption.rewardsProgram?.name,
            id: redemption.id,
          }
        }) ?? []
      }
      getRowId={() => random(0, 1000, true)}
    />
  )
}
